/**
 * @module  public/js/services/emailService.js
 * @summary Wrapper around the `sendUserEmail` Cloud Function.
 *
 * @description
 * Exposes a single helper that invokes the callable, returning the provider and
 * messageId or throwing if the status isn’t “sent”.
 *
 * ## Key Features
 * – Lazy initialises the Firebase callable.
 * – Central error surface so controllers stay slim.
 *
 * ## Public API
 * – `sendCustomEmail({ contactId, to, subject, html, taskId, fontFamily })`
 *
 * ## Dependencies
 * { firebase/functions }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-05-09
 */

import {
  getFunctions,
  httpsCallable,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-functions.js';
import { firebaseApp } from '../core/firebase-service.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';
import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import endpointsDefault from '../core/endpoints-service.js';
import ReconnectEmailBanner from '../ui/reconnectEmailBanner.js';
import { uploadTempEmailAttachmentsViaCallable } from '../utils/emailAttachmentUtils.js';

const functions = getFunctions(firebaseApp, 'us-central1');
const sendUserEmail = httpsCallable(functions, 'sendUserEmail');
const replyInThreadCF = httpsCallable(functions, 'replyInThread');
const uploadEmailTempAttachmentCF = httpsCallable(
  functions,
  'uploadEmailTempAttachment'
);

export default class EmailService {
  #fs;
  #auth;
  #connectionCache = new Map();
  #warmUpPromise = null;

  /**
   * @param {{ firestoreService: any, authService: any, apiClient?: import("../core/apiclient.js").default, endpoints?: import("../core/endpoints-service.js").EndpointService, reconnectBanner?: ReconnectEmailBanner }} deps
   */
  constructor({
    firestoreService,
    authService,
    apiClient,
    endpoints,
    reconnectBanner,
  } = {}) {
    this.#fs = firestoreService;
    this.#auth = authService;
    this.#api = apiClient ?? globalThis?.di?.apiClient ?? null;
    this.#endpoints =
      endpoints ?? globalThis?.di?.endpoints ?? endpointsDefault;
    this.#reconnectBanner = reconnectBanner ?? null;
  }

  #api;
  #endpoints;
  #reconnectBanner;

  #ensureBanner() {
    if (this.#reconnectBanner) return this.#reconnectBanner;
    if (!this.#api || !this.#endpoints) return null;
    this.#reconnectBanner = new ReconnectEmailBanner({
      apiClient: this.#api,
      endpoints: this.#endpoints,
    });
    return this.#reconnectBanner;
  }

  #handleEmailError(err, fallbackProvider = null) {
    try {
      const code = err?.details?.code || err?.code;
      if (code === 'EMAIL_OAUTH_DISCONNECTED') {
        const provider = err?.details?.provider || fallbackProvider || null;
        const uid = this.#auth?.currentUser?.uid;
        if (uid) this.#connectionCache.set(uid, false);
        this.#ensureBanner()?.show({ provider });
      }
    } catch (inner) {
      console.warn('[EmailService] failed to show reconnect banner', inner);
    }
  }

  /**
   * Determine whether the user has an active, non-revoked email OAuth token.
   *
   * Lookup order (stop at first hit):
   *   1. /tenants/{tenantId}/EmailOAuth/{uid}   – canonical, tenant-scoped
   *   2. /EmailOAuth/{uid}                      – transitional fallback (remove later)
   *   3. /Users/{uid}.emailConnected            – legacy flag
   *
   * @param {string=} uidOverride
   * @returns {Promise<boolean>}
   */
  async isConnected(uidOverride) {
    const uid = uidOverride || this.#auth?.currentUser?.uid;
    const debugEnabled = globalThis?.__FM_DEBUG === true;
    if (this.#connectionCache.has(uid)) {
      return this.#connectionCache.get(uid) === true;
    }
    if (debugEnabled) {
      console.groupCollapsed('[EmailService.isConnected]', uid);
    }

    try {
      if (!uid) {
        this.#connectionCache.set(uid, false);
        if (debugEnabled) console.log('no uid');
        return false;
      }

      /* ─────────────────────────── 1) tenant-scoped document ─────────────────────────── */
      const tenantId = await this.#fs.getTenantId(uid).catch(() => null);
      if (!tenantId) {
        this.#connectionCache.set(uid, false);
        if (debugEnabled) console.warn('tenantId not found for uid', uid);
        return false; // fail closed
      }

      const ref = docWithTenant(tenantId, `EmailOAuth/${uid}`);
      const snap = await getDoc(ref);
      let doc = snap.exists() ? snap.data() : null;
      if (debugEnabled) console.log(ref.path, !!doc);
      if (doc && doc.revoked !== true) {
        this.#connectionCache.set(uid, true);
        return true;
      }

      /* ─────────────────────────── 2) flat collection (legacy) ────────────────────────── */
      doc = await this.#fs.getDocument(`EmailOAuth/${uid}`);
      if (debugEnabled) console.log(`EmailOAuth/${uid}`, !!doc);
      if (doc && doc.revoked !== true) {
        this.#connectionCache.set(uid, true);
        return true;
      }

      /* ─────────────────────────── 3) Users/{uid}.emailConnected flag ─────────────────── */
      const legacy = await this.#fs.getDocument(`Users/${uid}`);
      if (debugEnabled) console.log(`Users/${uid}`, !!legacy);
      const connected = !!legacy?.emailConnected;
      this.#connectionCache.set(uid, connected);
      return connected;
    } catch (err) {
      if (debugEnabled) {
        console.error('[EmailService.isConnected] error:', err);
      }
      this.#connectionCache.set(uid, false);
      return false; // fail safe
    } finally {
      if (debugEnabled) console.groupEnd();
    }
  }

  /**
   * Warm the sendUserEmail callable without sending a message.
   *
   * @param {{reason?: string}} [options={}] - Warmup metadata for diagnostics.
   * @returns {Promise<Record<string, unknown>>} Callable warmup response data.
   */
  async warmUp(options = {}) {
    if (this.#warmUpPromise) return this.#warmUpPromise;
    const reason =
      typeof options.reason === 'string' && options.reason.trim()
        ? options.reason.trim()
        : 'focusmode-start';
    this.#warmUpPromise = Promise.all([
      sendUserEmail({ action: 'warmup', reason }),
      replyInThreadCF({ action: 'warmup', reason }),
    ])
      .then(([sendRes, replyRes]) => ({
        status: 'warmed',
        sendUserEmail: sendRes?.data || { status: 'warmed' },
        replyInThread: replyRes?.data || { status: 'warmed' },
      }))
      .catch((err) => {
        this.#warmUpPromise = null;
        this.#handleEmailError(err);
        throw err;
      });
    return this.#warmUpPromise;
  }

  /**
   * @param {{ contactId, to, subject, html, taskId, fontFamily?: string }} payload
   * @returns {{provider:string,messageId:string,threadId:string}}
   */
  async sendCustomEmail(payload) {
    const { adHocAttachments = [], ...callablePayload } = payload || {};
    let uploadedRefs = [];
    try {
      uploadedRefs = await this.#uploadAdHocAttachments(adHocAttachments);
      const res = await sendUserEmail({
        ...callablePayload,
        ...(uploadedRefs.length ? { adHocAttachmentRefs: uploadedRefs } : {}),
      });
      if (res.data.status !== 'sent') {
        throw new Error('Email send failed: ' + JSON.stringify(res.data));
      }
      return {
        provider: res.data.provider,
        messageId: res.data.messageId,
        threadId: res.data.threadId,
      };
    } catch (err) {
      this.#handleEmailError(err);
      throw err;
    }
  }

  /**
   * @param {{taskId:string, html:string, fontFamily?:string}} payload
   * @returns {{status,provider,messageId}}
   */
  async replyInThread(payload) {
    const { adHocAttachments = [], ...callablePayload } = payload || {};
    let uploadedRefs = [];
    try {
      uploadedRefs = await this.#uploadAdHocAttachments(adHocAttachments);
      const res = await replyInThreadCF({
        ...callablePayload,
        ...(uploadedRefs.length ? { adHocAttachmentRefs: uploadedRefs } : {}),
      });
      if (res.data.status !== 'sent') {
        throw new Error('Email send failed: ' + JSON.stringify(res.data));
      }
      return res.data;
    } catch (err) {
      this.#handleEmailError(err, callablePayload?.provider);
      throw err;
    }
  }

  /**
   * Upload ad hoc File attachments to short-lived Storage objects.
   *
   * @param {File[]} files Selected browser files.
   * @returns {Promise<object[]>} Callable-safe attachment refs.
   */
  async #uploadAdHocAttachments(files = []) {
    const selected = Array.from(files || []);
    if (!selected.length) return [];
    const uid = this.#auth?.currentUser?.uid;
    if (!uid) throw new Error('User must be signed in to attach files.');
    const tenantId = await this.#fs.getTenantId(uid);
    if (!tenantId) throw new Error('Tenant is required to attach files.');
    return uploadTempEmailAttachmentsViaCallable({
      files: selected,
      tenantId,
      uploadCallable: uploadEmailTempAttachmentCF,
    });
  }
}
